import './filter-bar.css';
import { DatePicker, Dropdown, Input, Popover, Select, Tag } from 'antd';
import { PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { fmtDate, fmtDateApi } from '@extensions/utils';

// Строка поиска с фильтрами-чипсами.
// props:
//   filters  — описание фильтров: [{ key, label, options: [{ value, label }], emptyValue }]
//              или { key, label, type: 'range', emptyValue } — период [beg, end] (yyyy-MM-dd); при добавлении — текущий месяц
//              или { key, label, type: 'search', emptyValue, options, search(text) => Promise<[{value,label}]>, placeholder } —
//              значение ищется на сервере; options — уже известные значения для подписи чипа
//              выбранное значение хранится в value[key]; фильтр считается выключенным, если value[key] === emptyValue (по умолчанию null)
//              клик по чипу открывает список значений — смена значения фильтра
//   value    — текущие значения { [key]: value }
//   onChange(patch) — изменение фильтров
//   onSearch(text)  — поиск при вводе (с задержкой debounce мс), по Enter — сразу; searchValue — текущий текст поиска (например из URL)
//   placeholder, extra — дополнительные элементы справа
const FilterBar = ({ filters, value, onChange, onSearch, searchValue = '', placeholder = 'Поиск', extra, debounce = 400 }) => {
    const [text, setText] = useState(searchValue);
    const [openSearch, setOpenSearch] = useState(null);
    const [searchResults, setSearchResults] = useState({});
    const timer = useRef(null);
    const searchTimer = useRef(null);
    useEffect(() => { setText(searchValue); }, [searchValue]);
    useEffect(() => () => { clearTimeout(timer.current); clearTimeout(searchTimer.current); }, []);

    const isActive = (f) => value[f.key] !== (f.emptyValue ?? null) && value[f.key] != null;

    const chipLabel = (f) => {
        if (f.type === 'range') {
            const [a, b] = value[f.key] ?? [];
            return `${a ? fmtDate(a) : '…'} – ${b ? fmtDate(b) : '…'}`;
        }
        const opt = f.options.find((o) => o.value === value[f.key]);
        if (f.type === 'search') return opt?.label ?? `#${value[f.key]}`;
        return opt?.label ?? String(value[f.key]);
    };

    const menuItems = useMemo(() => filters.map((f) => (
        f.type === 'range' || f.type === 'search'
            ? { key: `${f.key}:${f.type}`, label: f.label }
            : {
                key: f.key,
                label: f.label,
                children: f.options.map((o) => ({ key: `${f.key}:${String(o.value)}`, label: o.label })),
            }
    )), [filters]);

    const onMenuClick = ({ key }) => {
        const idx = key.indexOf(':');
        const fKey = key.slice(0, idx);
        const raw = key.slice(idx + 1);
        const f = filters.find((d) => d.key === fKey);
        if (!f) return;
        if (f.type === 'range') {
            onChange({ [fKey]: [fmtDateApi(dayjs().startOf('month')), fmtDateApi(dayjs().endOf('month'))] });
            return;
        }
        if (f.type === 'search') {
            setOpenSearch(f.key);
            return;
        }
        const opt = f.options.find((o) => String(o.value) === raw);
        if (opt) onChange({ [fKey]: opt.value });
    };

    const submit = (v) => {
        clearTimeout(timer.current);
        setText(v);
        onSearch?.(v);
    };

    const onInput = (v) => {
        setText(v);
        clearTimeout(timer.current);
        if (v === '') onSearch?.('');
        else timer.current = setTimeout(() => onSearch?.(v), debounce);
    };

    const onSearchText = (f, v) => {
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            Promise.resolve(f.search(v))
                .then((list) => setSearchResults((prev) => ({ ...prev, [f.key]: list })))
                .catch(() => {});
        }, 300);
    };

    const searchSelect = (f) => (
        <Select
            showSearch
            filterOption={false}
            onSearch={(v) => onSearchText(f, v)}
            options={searchResults[f.key] ?? f.options}
            notFoundContent={null}
            style={{ width: 320 }}
            autoFocus
            defaultOpen
            placeholder={f.placeholder ?? 'Поиск'}
            value={value[f.key] || undefined}
            onChange={(v) => { onChange({ [f.key]: v }); setOpenSearch(null); }}
        />
    );

    const renderChip = (f) => {
        const chip = (
            <Tag
                key={f.key}
                closable
                className="filter-bar-chip"
                onClose={(e) => { e.preventDefault(); e.stopPropagation(); onChange({ [f.key]: f.emptyValue ?? null }); }}
            >
                <span className="filter-bar-chip-label">{f.label}:</span> {chipLabel(f)}
            </Tag>
        );
        if (f.type === 'search') {
            return (
                <Popover
                    key={f.key}
                    trigger="click"
                    open={openSearch === f.key}
                    onOpenChange={(o) => setOpenSearch(o ? f.key : null)}
                    content={searchSelect(f)}
                >
                    {chip}
                </Popover>
            );
        }
        if (f.type !== 'range') {
            return (
                <Dropdown
                    key={f.key}
                    trigger={['click']}
                    menu={{
                        items: f.options.map((o) => ({ key: String(o.value), label: o.label })),
                        selectable: true,
                        selectedKeys: [String(value[f.key])],
                        onClick: ({ key }) => {
                            const opt = f.options.find((o) => String(o.value) === key);
                            if (opt) onChange({ [f.key]: opt.value });
                        },
                    }}
                >
                    {chip}
                </Dropdown>
            );
        }
        const [a, b] = value[f.key] ?? [];
        return (
            <Popover
                key={f.key}
                trigger="click"
                content={(
                    <DatePicker.RangePicker
                        format="DD.MM.YYYY"
                        allowEmpty={[true, true]}
                        value={[a ? dayjs(a) : null, b ? dayjs(b) : null]}
                        onChange={(d) => onChange({ [f.key]: d ? [fmtDateApi(d[0]) || null, fmtDateApi(d[1]) || null] : (f.emptyValue ?? null) })}
                    />
                )}
            >
                {chip}
            </Popover>
        );
    };

    const active = filters.filter(isActive);

    return (
        <div className="filter-bar">
            <SearchOutlined className="filter-bar-icon" />
            {active.map(renderChip)}
            {filters.filter((f) => f.type === 'search' && openSearch === f.key && !isActive(f)).map((f) => (
                <Popover
                    key={f.key}
                    open
                    onOpenChange={(o) => { if (!o) setOpenSearch(null); }}
                    content={searchSelect(f)}
                >
                    <Tag className="filter-bar-chip">
                        <span className="filter-bar-chip-label">{f.label}:</span> …
                    </Tag>
                </Popover>
            ))}
            <Input
                variant="borderless"
                className="filter-bar-input"
                placeholder={active.length ? '' : placeholder}
                value={text}
                allowClear
                onChange={(e) => onInput(e.target.value)}
                onPressEnter={() => submit(text)}
            />
            <Dropdown menu={{ items: menuItems, onClick: onMenuClick }} trigger={['click']}>
                <PlusCircleOutlined className="filter-bar-add" title="Добавить фильтр" />
            </Dropdown>
            {extra && <div className="filter-bar-extra">{extra}</div>}
        </div>
    );
};

export default FilterBar;
